import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { DerivativeSearchValue, LeverageProductType } from '@zero/shared/util-models';
import { filter, take } from 'rxjs';
import { DerivativeSearchActions } from '../+state/derivative-search.actions';
import { derivativeSearchFeature } from '../+state/derivative-search.reducer';
import { CAPS, DERIVATIVE_TYPES, DURATIONS, FACTORS, LEVERAGES, STRIKES } from '../entities/defaults';
import { DerivativeType } from '../entities/derivative.type';

@Injectable({
  providedIn: 'root',
})
export class DerivativeSearchFacade {
  readonly #store: Store = inject(Store);

  public readonly underlyingIsin$ = this.#store.select(derivativeSearchFeature.selectIsin);
  public readonly products$ = this.#store.select(derivativeSearchFeature.selectProducts);
  public readonly currentDerivativeType$ = this.#store.select(derivativeSearchFeature.selectDerivativeType);
  public readonly currentCap$ = this.#store.select(derivativeSearchFeature.selectCap);
  public readonly currentDuration$ = this.#store.select(derivativeSearchFeature.selectDuration);
  public readonly currentStrike$ = this.#store.select(derivativeSearchFeature.selectStrike);
  public readonly currentFactor$ = this.#store.select(derivativeSearchFeature.selectFactor);
  public readonly currentLeverage$ = this.#store.select(derivativeSearchFeature.selectLeverage);
  public readonly productType$ = this.#store.select(derivativeSearchFeature.selectProductType);
  public readonly quickMode$ = this.#store.select(derivativeSearchFeature.selectQuickMode);
  public readonly highlightedProduct$ = this.#store.select(derivativeSearchFeature.selectHighlightedProduct);

  public readonly DERIVATIVE_TYPES: DerivativeType[] = DERIVATIVE_TYPES;
  public readonly LEVERAGES = LEVERAGES;
  public readonly CAPS: DerivativeSearchValue[] = CAPS;
  public readonly STRIKES: DerivativeSearchValue[] = STRIKES;
  public readonly DURATIONS: DerivativeSearchValue[] = DURATIONS;
  public readonly FACTORS: DerivativeSearchValue[] = FACTORS;
  public readonly MAX_CUSTOM_LEVERAGE = 200;

  public customLeverage: number;

  public isValidCustomLeverage = (): boolean =>
    Boolean(this.customLeverage && this.customLeverage > 1 && this.customLeverage <= this.MAX_CUSTOM_LEVERAGE);

  public setup = (isin: string): void => this.#store.dispatch(DerivativeSearchActions.setIsin({ isin }));

  public changeUnderlying = (isin: string): void => this.setup(isin);

  public clear = (): void => this.#store.dispatch(DerivativeSearchActions.resetState());

  public triggerSearch = (): void => this.#store.dispatch(DerivativeSearchActions.loadProducts());

  public toggleDerivativeType = (derivativeType: DerivativeType): void => {
    this.currentDerivativeType$
      .pipe(
        take(1),
        filter((currentDerivativeType) => currentDerivativeType !== derivativeType),
      )
      .subscribe(() => this.#store.dispatch(DerivativeSearchActions.setDerivativeType({ derivativeType })));
  };

  public toggleLeverageType = (leverageProductType: LeverageProductType): void => {
    this.productType$
      .pipe(
        take(1),
        filter((currentProductType) => currentProductType !== leverageProductType),
      )
      .subscribe(() => this.#store.dispatch(DerivativeSearchActions.setLongOrShort({ leverageProductType })));
  };

  public useStrike = (strike: DerivativeSearchValue): void => this.#store.dispatch(DerivativeSearchActions.setStrike({ strike }));
  public useDuration = (duration: DerivativeSearchValue): void => this.#store.dispatch(DerivativeSearchActions.setDuration({ duration }));
  public useLeverage = (leverage: number): void => this.#store.dispatch(DerivativeSearchActions.setLeverage({ leverage }));
  public useFactor = (factor: DerivativeSearchValue): void => this.#store.dispatch(DerivativeSearchActions.setFactor({ factor }));
  public useCap = (cap: DerivativeSearchValue): void => this.#store.dispatch(DerivativeSearchActions.setCap({ cap }));
  public toggleQuickMode = (quickMode: boolean): void => this.#store.dispatch(DerivativeSearchActions.setQuickMode({ quickMode }));

  public useCustomLeverage = (): void => {
    if (!this.isValidCustomLeverage()) {
      return;
    }
    this.customLeverage = Math.floor(this.customLeverage);
    this.useLeverage(this.customLeverage);
    if (!this.LEVERAGES.includes(this.customLeverage)) {
      this.LEVERAGES[3] = this.customLeverage;
    }
  };

  public changeCustomLeverage = (event: KeyboardEvent): void => {
    const value = (event.target as HTMLInputElement)?.value;
    if (!value) {
      this.customLeverage = undefined;
      return;
    }
    if (isNaN(Number(value))) {
      return;
    }
    const leverage = Number(value);
    this.customLeverage = leverage < 0 ? leverage * -1 : leverage;
  };
}
